import React, { useState } from 'react';
import CollapsibleTable from '../../../components/CollapsibleTable';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import { CheckCircle, AlertTriangle, TrendingUp, Eye, Users, Package, Award, ShoppingCart, Zap, MessageSquare, Layout, Palette, Leaf, Lightbulb, Clock, Info, ChevronDown, ThumbsUp, ThumbsDown } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, ResponsiveContainer } from 'recharts';
import BarChart from '../../../components/BarChart';
import { ContentData } from '@/utils/taskToUseCaseMap';

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper to build full URL for images (handles API paths and external URLs)
const buildImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

// Types for prioritized recommendations and strategic considerations
interface PrioritizedRecommendation {
  priority: string;
  element: string;
  current_state?: string;
  recommendation: string;
  implementation: string;
  expected_impact: string;
  rationale?: string;
}

interface StrategicConsideration {
  aspect: string;
  opportunity: string;
}

// Types for design analysis framework
interface MessagingCopyAnalysis {
  headlines: string;
  value_propositions: string;
  call_to_actions: string;
  tone_of_voice: string;
}

interface FunctionalDesignAnalysis {
  information_architecture: string;
  shelf_impact: string;
  usability: string;
}

interface BrandExpressionAnalysis {
  brand_consistency: string;
  category_conventions: string;
  premium_value_signals: string;
}

interface SustainabilityAnalysis {
  environmental_messaging: string;
  material_perception: string;
  social_responsibility: string;
}

interface TechnicalAnalysisOutput {
  image_index: number;
  readability_score?: { score: number; reason: string };
  claim_hierarchy_score?: { score: number; reason: string };
  brand_guideline_compliance?: string;
  risk_flags?: string[];
  technical_notes?: string;
  messaging_copy?: MessagingCopyAnalysis;
  functional_design?: FunctionalDesignAnalysis;
  brand_expression?: BrandExpressionAnalysis;
  sustainability?: SustainabilityAnalysis;
  [key: string]: any;
}

interface FinalInsights {
  overall_packaging_score?: number;
  key_strengths?: string[];
  key_weaknesses?: string[];
  critical_actions?: string[];
  opportunity_actions?: string[];
  segment_insights?: string[];
  prioritized_recommendations?: PrioritizedRecommendation[];
  strategic_considerations?: StrategicConsideration[];
  [key: string]: any;
}

// Shared types for panel analysis
interface ThemeWithCount {
  theme: string;
  count: number;
  percentage: number;
  example_quotes?: string[];
}

// Side Panel Analysis types
interface SideActionItem {
  change: string;
  why: string;
  expected_outcome: string;
}

interface SideInsights {
  utilization_rate: number;
  avg_relevance_score: number;
  common_missing_info: string[];
  opportunity_areas: string[];
  what_works: string[];
  strategic_recommendation: string;
  action_plan: SideActionItem[];
  quotes?: {
    representative_positive: string[];
    representative_negative: string[];
  };
  // Theme aggregations for Conversion Jobs & Winning Patterns
  usage_confidence_themes?: ThemeWithCount[];
  trust_badge_themes?: ThemeWithCount[];
  interactive_elements_themes?: ThemeWithCount[];
}

// Back Panel Analysis types
interface BackActionItem {
  change: string;
  why: string;
  expected_outcome: string;
}

// Technical Storytelling dimension scores (0-5 scale each)
interface TechnicalStorytellingScores {
  mechanism_clarity: number;
  language_accessibility: number;
  benefits_ingredients_connection: number;
  visual_aids_helpfulness: number;
  scannability: number;
  credibility_specifics: number;
}

interface BackInsights {
  trust_score: number;
  conversion_rate: number;
  avg_readability_score: number;
  core_barrier: string;
  top_accelerators: ThemeWithCount[];
  top_barriers: ThemeWithCount[];
  readability_issues: string[];
  findability_issues: string[];
  trust_builders: string[];
  trust_breakers: string[];
  action_plan: BackActionItem[];
  quotes?: {
    representative_positive: string[];
    representative_negative: string[];
  };
  // Theme aggregations for Conversion Jobs & Winning Patterns
  purchase_anxiety_themes?: ThemeWithCount[];
  brand_values_themes?: ThemeWithCount[];
  values_alignment_themes?: ThemeWithCount[];
  repeat_purchase_themes?: ThemeWithCount[];
  routine_fit_themes?: ThemeWithCount[];
  brand_story_resonance_themes?: ThemeWithCount[];
  guarantee_presence_themes?: ThemeWithCount[];
  cross_sell_presence_themes?: ThemeWithCount[];
  // Technical Storytelling Assessment
  technical_storytelling_avg_scores?: TechnicalStorytellingScores;
  technical_storytelling_total_score?: number;
  technical_storytelling_grade?: string;
  technical_storytelling_key_strengths?: string[];
  technical_storytelling_key_weaknesses?: string[];
}

// Shopper Conversion Checklist types
interface ConversionJobScore {
  job_name: string;
  shopper_question: string;
  score: number;
  score_rationale: string;
  evidence: string[];
  improvement_suggestions: string[];
}

interface ConversionJobsScorecard {
  instant_comprehension: ConversionJobScore;
  proof_credibility: ConversionJobScore;
  risk_reduction: ConversionJobScore;
  usage_certainty: ConversionJobScore;
  differentiation_positioning: ConversionJobScore;
  value_justification: ConversionJobScore;
  identity_values_alignment: ConversionJobScore;
  usage_occasion_habit: ConversionJobScore;
  total_score: number;
  overall_grade: string;
  top_performing_jobs: string[];
  priority_improvement_jobs: string[];
}

// Winning Patterns Checklist types
interface WinningPatternScore {
  pattern_name: string;
  what_winners_do: string;
  is_present: boolean;
  effectiveness: number;
  evidence: string;
  recommendation: string;
}

interface WinningPatternsChecklist {
  single_bold_promise: WinningPatternScore;
  proof_cluster: WinningPatternScore;
  benefit_bullets: WinningPatternScore;
  brand_story_block: WinningPatternScore;
  ingredient_transparency: WinningPatternScore;
  usage_instructions: WinningPatternScore;
  risk_reversal: WinningPatternScore;
  visual_hierarchy: WinningPatternScore;
  emotional_imagery: WinningPatternScore;
  category_must_haves: WinningPatternScore;
  interactive_elements: WinningPatternScore;
  cross_sell_cues: WinningPatternScore;
  patterns_present_count: number;
  total_score: number;
  overall_grade: string;
  top_performing_patterns: string[];
  priority_improvement_patterns: string[];
}

// Competitive Intelligence types
interface ProductDimensionScore {
  product_name: string;
  score: number;
  rationale: string;
}

interface DimensionScoreResult {
  dimension_key: string;
  product_scores: ProductDimensionScore[];
  category_insight: string;
}


interface CompetitiveTakeaway {
  competitive_threats: string;
  our_advantages: string;
  borrowable_equities: string;
  key_tension: string;
}

interface CompetitiveIntelligence {
  target_product_name: string;
  dimension_scores: DimensionScoreResult[];
  competitive_takeaway: CompetitiveTakeaway;
}

interface PackagingReviewV2Data {
  technical_and_compliance_analysis?: {
    technical_and_compliance_analysis_outputs: Array<TechnicalAnalysisOutput>;
  };
  shelf_test_responses?: {
    persona_responses: Array<{
      persona_id?: number;
      [key: string]: any;
    }>;
  };
  shelf_image?: string;  // Programmatic shelf with coordinate-based heatmap
  persona_reaction_responses?: {
    persona_responses: Array<{
      persona_id?: number;
      image_index?: number;
      [key: string]: any;
    }>;
  };
  technical_compliance_insights?: any;
  shelf_test_insights?: any;
  persona_reaction_insights?: any;
  final_insights?: FinalInsights;
  packaging_images?: string[];  // Array of packaging images for rendering in tables
  competitor_images?: string[];  // Array of competitor images for rendering in tables
  packaging_like_heatmaps?: string[];  // Heatmaps showing elements personas liked
  packaging_dislike_heatmaps?: string[];  // Heatmaps showing elements personas disliked
  heatmap_legend?: string;  // Shared legend for both like/dislike heatmaps
  side_responses?: any;
  side_insights?: SideInsights;
  back_responses?: any;
  back_insights?: BackInsights;
  conversion_jobs_scorecard?: ConversionJobsScorecard;  // Expert evaluation of 8 conversion jobs
  winning_patterns_checklist?: WinningPatternsChecklist;  // Expert evaluation of 12 winning patterns
  competitive_intelligence?: CompetitiveIntelligence;  // Front-panel competitive analysis
}

interface PackagingReviewV2ResultsProps {
  data: PackagingReviewV2Data;
  contentData?: ContentData;  // Simulation inputs
  isPreparingExport?: boolean;
  onImageClick?: (src: string, alt: string) => void;
}

const PackagingReviewV2Results: React.FC<PackagingReviewV2ResultsProps> = ({
  data,
  contentData,
  isPreparingExport = false
}) => {
  console.log("PackagingReviewV2Results rendering with data:", data, "and contentData:", contentData);
  // Convert array of objects to CollapsibleTable format
  const convertToTableData = (responses: any[], title: string) => {
    if (!responses || responses.length === 0) return null;
    
    const allKeys = new Set<string>();
    responses.forEach(response => {
      Object.keys(response).forEach(key => allKeys.add(key));
    });
    
    const headers = Array.from(allKeys).sort((a, b) => {
      if (a === 'persona_id' || a === 'image_index') return -1;
      if (b === 'persona_id' || b === 'image_index') return 1;
      return a.localeCompare(b);
    }).map(key => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    
    const keyOrder = Array.from(allKeys).sort((a, b) => {
      if (a === 'persona_id' || a === 'image_index') return -1;
      if (b === 'persona_id' || b === 'image_index') return 1;
      return a.localeCompare(b);
    });
    
    const tableData = responses.map(response => {
      const row: Record<string, string> = {};
      keyOrder.forEach((key, idx) => {
        const value = response[key];
        const header = headers[idx];
        
        if (value === null || value === undefined) {
          row[header] = '';
        } else if (typeof value === 'object') {
          row[header] = JSON.stringify(value, null, 2);
        } else {
          row[header] = String(value);
        }
      });
      return row;
    });
    
    return {
      type: 'table' as const,
      title,
      headers,
      data: tableData
    };
  };

  // ============================================
  // DESIGN SYSTEM - Consistent Styling Throughout
  // ============================================
  
  // Colors
  const colors = {
    primary: '#03e8d3', // Brand color
    success: '#10B981', // green-500
    warning: '#F59E0B', // yellow-500
    danger: '#EF4444', // red-500
    purple: '#8B5CF6', // purple-500
    indigo: '#6366F1', // indigo-500
  };

  // Typography (Tailwind classes)
  const typography = {
    // Section titles (main heading for each analysis section)
    sectionTitle: 'text-xl font-semibold text-gray-900',
    
    // Subsection titles (h3 - within sections)
    subsectionTitle: 'text-lg font-semibold text-gray-900',
    
    // Card titles (smaller headings within cards)
    cardTitle: 'text-base font-semibold text-gray-900',
    
    // Small titles (for lists, small sections)
    smallTitle: 'text-sm font-semibold text-gray-800',
    
    // Body text (default paragraph text)
    body: 'text-sm text-gray-700',
    
    // Description/helper text (smaller, lighter)
    description: 'text-sm text-gray-600',
    
    // Small description (captions, metadata)
    smallDescription: 'text-xs text-gray-500',
    
    // Labels
    label: 'text-xs font-semibold text-gray-700 uppercase tracking-wider',
  };

  // Spacing (Tailwind classes)
  const spacing = {
    // Section spacing (between major sections)
    sectionGap: 'mb-6',
    
    // Subsection spacing (between subsections within a section)
    subsectionGap: 'mb-6',
    
    // Card spacing (between cards in a grid)
    cardGap: 'gap-4',
    
    // Item spacing (between list items, small elements)
    itemGap: 'space-y-2',
    
    // Padding for sections
    sectionPadding: 'p-6',
    
    // Padding for cards
    cardPadding: 'p-4',
    
    // Padding for small elements
    smallPadding: 'p-3',
  };

  // Border & Background (Tailwind classes)
  const styling = {
    // Main section container
    section: 'bg-white rounded-lg border border-gray-200',
    
    // Card container
    card: 'bg-white border border-gray-200 rounded-lg',
    
    // Nested card (within sections)
    nestedCard: 'bg-white border border-gray-200 rounded',
    
    // Divider
    divider: 'border-b border-gray-200',
  };

  // Dimension name mapping - Convert technical field names to user-friendly display names
  const dimensionNameMap: Record<string, string> = {
    visual: 'Visual Appeal',
    clarity: 'Clarity',
    trust: 'Trust',
    value: 'Value Perception',
    intent: 'Purchase Intent',
    fit: 'Lifestyle Fit',
    natural: 'Natural Feel',
    modern: 'Modern Design',
    premium: 'Premium Quality',
  };

  // Helper function to get display name for dimension
  const getDimensionDisplayName = (dimension: string): string => {
    return dimensionNameMap[dimension.toLowerCase()] || dimension;
  };

  // Helper function to format text with bold prefix if it starts with "text:"
  const formatTextWithBoldPrefix = (text: string): string => {
    // If text already has bold markdown at the start, return as-is
    if (text.startsWith('**')) {
      return text;
    }

    // Match pattern: text followed by colon at the start
    const match = text.match(/^([^:]+):\s*(.*)$/);
    if (match) {
      const [, prefix, rest] = match;
      return `**${prefix}:**${rest ? ' ' + rest : ''}`;
    }
    return text;
  };

  // Render visual score card with circular progress
  const renderScoreCard = (score: number | string, label: string, icon?: React.ReactNode, maxScore: number = 10) => {
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    const percentage = (numScore / maxScore) * 100;
    const color = numScore >= 8 ? colors.success :
                  numScore >= 6 ? colors.warning :
                  colors.danger;
    
    const bgColor = numScore >= 8 ? 'bg-green-50' :
                    numScore >= 6 ? 'bg-yellow-50' :
                    'bg-red-50';
    
    return (
      <div className={`${bgColor} rounded-lg p-4 border-2 ${numScore >= 8 ? 'border-green-200' : numScore >= 6 ? 'border-yellow-200' : 'border-red-200'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            {icon && <span className="mr-2" style={{ color }}>{icon}</span>}
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </div>
          <span className="text-2xl font-bold" style={{ color }}>{numScore.toFixed(1)}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
        <div className="text-xs text-gray-500 mt-1 text-right">out of {maxScore}</div>
      </div>
    );
  };

  // Render metric card
  const renderMetricCard = (value: string | number, label: string, icon: React.ReactNode, colorClass: string) => {
    return (
      <div className={`${colorClass} rounded-lg p-4 border-2`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-gray-900">{value}</div>
            <div className="text-sm text-gray-600 mt-1">{label}</div>
          </div>
          <div className="text-gray-400">{icon}</div>
        </div>
      </div>
    );
  };

  // Render list items with markdown support
  const renderList = (items: string[] | undefined, icon?: React.ReactNode, color?: string) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className={spacing.itemGap}>
        {items.map((item, idx) => (
          <div key={idx} className="flex items-start">
            {icon ? (
              <span className={`flex-shrink-0 mt-0.5 mr-2 ${color || 'text-gray-400'}`}>
                {icon}
              </span>
            ) : (
              <span className="mr-2 text-gray-400 flex-shrink-0">•</span>
            )}
            <div className={`${typography.body} flex-1`}>
              <MarkdownRenderer content={formatTextWithBoldPrefix(item)} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Job descriptions - what each conversion job evaluates
  const jobDescriptions: Record<string, string> = {
    'Instant Comprehension': 'Measures how quickly shoppers can grasp the main benefit and value proposition within 5 seconds of viewing',
    'Proof & Credibility': 'Evaluates presence of trust-building elements like certifications, clinical studies, endorsements, and substantiated claims',
    'Risk Reduction': 'Assesses how well packaging reduces purchase anxiety through guarantees, safety messaging, and ingredient transparency',
    'Usage Certainty': 'Measures clarity of usage instructions, dosage guidance, and tips that build confidence in achieving results',
    'Differentiation & Positioning': 'Evaluates how clearly the product stands out from competitors with a unique, ownable value proposition',
    'Value Justification': 'Assesses whether packaging signals justify the price point through quality cues and value messaging',
    'Identity & Values Alignment': 'Measures how well brand personality and values messaging resonates with the target audience',
    'Usage Occasion & Habit Formation': 'Evaluates positioning for routine usage, repeat purchase cues, and habit-building messaging'
  };

  // Pattern descriptions - what each winning pattern means
  const patternDescriptions: Record<string, string> = {
    'Single Bold Promise': 'One clear, unmissable hero claim rather than multiple competing messages',
    'Proof Cluster': 'Trust badges, certifications, and credentials grouped together for maximum impact',
    'Benefit Bullets': 'Scannable list of key benefits, often with icons for quick comprehension',
    'Brand Story Block': 'Short mission or origin story that creates emotional connection with shoppers',
    'Ingredient Transparency': 'Clear "what\'s inside" information with ingredient benefits explained',
    'Usage Instructions': 'Simple, clear directions that build confidence in product usage',
    'Risk Reversal': 'Guarantees, safety claims, or money-back promises that reduce purchase anxiety',
    'Visual Hierarchy': 'Design that guides the eye through information in the optimal order',
    'Emotional Imagery': 'Photos or illustrations that create desire and emotional connection',
    'Category Must-Haves': 'Required elements that shoppers expect for this specific product category',
    'Interactive Elements': 'QR codes, AR experiences, or social handles that extend engagement',
    'Cross-Sell Cues': 'Messaging that encourages trying other products in the line'
  };

  // State for collapsible sections
  const [showDetailedAnalysis, setShowDetailedAnalysis] = React.useState(false);
  // const [showTechnicalTable, setShowTechnicalTable] = React.useState(false); // Commented out - technical table removed
  const [showShelfTable, setShowShelfTable] = React.useState(false);
  const [showReactionTable, setShowReactionTable] = React.useState(false);

  // State for Conversion Jobs and Winning Patterns expanded views
  const [showConversionJobsDetails, setShowConversionJobsDetails] = React.useState(false);
  const [showWinningPatternsDetails, setShowWinningPatternsDetails] = React.useState(false);

  // State for radar chart tooltip
  const [radarTooltip, setRadarTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [conversionJobsRadarTooltip, setConversionJobsRadarTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [winningPatternsRadarTooltip, setWinningPatternsRadarTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Derived values
  const brandName = contentData?.name || contentData?.data?.brand || 'Product';

  return (
    <div className="space-y-6">
      {/* Final Insights Overview - SHOWN FIRST */}
      {data.final_insights && (
        <div className={`${styling.section} ${spacing.sectionPadding}`}>
          <div className={`flex items-center mb-4 pb-4 ${styling.divider}`}>
            <Award className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className={typography.sectionTitle}>{brandName}</h2>
          </div>

          {/* Hero Section: Packaging Image + Score */}
          <div className={`${spacing.subsectionGap}`}>
            {/* Front Packaging Image - Centered */}
            <div>
              <h3 className={`${typography.cardTitle} mb-3 text-center`}>Front View</h3>
              <div className="flex justify-center">
                {data.packaging_images?.[0] ? (
                  <img
                    src={buildImageUrl(data.packaging_images[0])}
                    alt={`${brandName} packaging front view`}
                    className="max-h-[500px] w-auto rounded shadow-lg"
                  />
                ) : (
                  <div className="bg-gray-50 rounded-lg p-12 border border-gray-200">
                    <div className="text-gray-400 text-center">
                      <Package className="w-16 h-16 mx-auto mb-2" />
                      <span className="text-sm">No packaging image</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Packaging Views - Grid */}
            {data.packaging_images && data.packaging_images.length > 1 && (
              <div className="mt-6">
                <h3 className={`${typography.cardTitle} mb-3 text-center`}>Additional Views</h3>
                <div className={`grid grid-cols-2 ${spacing.cardGap} justify-items-center`}>
                  {data.packaging_images.slice(1).map((image, idx) => (
                    <div key={idx} className="flex justify-center">
                      <img
                        src={buildImageUrl(image)}
                        alt={`${brandName} packaging additional view ${idx + 1}`}
                        className="max-h-[300px] w-auto rounded shadow-md border border-gray-200"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Score - Below Image */}
            {data.final_insights.overall_packaging_score !== undefined && (
              <div className="text-center mt-6">
                <div className="text-5xl font-bold mb-2"
                     style={{
                       color: data.final_insights.overall_packaging_score >= 8 ? colors.success :
                             data.final_insights.overall_packaging_score >= 6 ? colors.warning : colors.danger
                     }}>
                  {data.final_insights.overall_packaging_score.toFixed(1)}
                  <span className="text-2xl text-gray-400">/10</span>
                </div>
                <div className={typography.description}>Overall Packaging Performance</div>
                <div className={`${typography.smallDescription} mt-1`}>Composite score across technical, shelf, and consumer dimensions</div>
              </div>
            )}
          </div>

          {/* Key Strengths & Areas to Focus - Side by Side */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
            {/* Key Strengths */}
            {data.final_insights.key_strengths && data.final_insights.key_strengths.length > 0 && (
              <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Key Strengths
                </h3>
                <div className={spacing.itemGap}>
                  {data.final_insights.key_strengths.map((item: string, idx: number) => (
                    <div key={idx} className={`flex items-baseline ${typography.body}`}>
                      <span className="mr-2 text-green-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(item)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas to Focus */}
            {data.final_insights.key_weaknesses && data.final_insights.key_weaknesses.length > 0 && (
              <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                  Areas to Focus
                </h3>
                <div className={spacing.itemGap}>
                  {data.final_insights.key_weaknesses.map((item: string, idx: number) => (
                    <div key={idx} className={`flex items-baseline ${typography.body}`}>
                      <span className="mr-2 text-red-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(item)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Recommendations */}
          {data.final_insights.prioritized_recommendations && data.final_insights.prioritized_recommendations.length > 0 && (
            <div className={`${styling.nestedCard} ${spacing.cardPadding} ${spacing.subsectionGap} border-l-4 border-l-indigo-500`}>
              <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                <Zap className="w-4 h-4 mr-2 text-indigo-600" />
                Recommendations
              </h3>
              <p className={`${typography.smallDescription} mb-4`}>Suggestions to improve packaging performance</p>

              {/* Critical Recommendations */}
              {(() => {
                const criticalRecs = data.final_insights.prioritized_recommendations?.filter(
                  (rec: PrioritizedRecommendation) => rec.priority === 'Critical'
                ) || [];
                return criticalRecs.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      High Impact
                    </h4>
                    <div className={spacing.itemGap}>
                      {criticalRecs.map((rec: PrioritizedRecommendation, idx: number) => (
                        <div key={idx} className="p-3 bg-red-50 rounded border border-red-200">
                          <div className="font-semibold text-red-900 text-sm mb-1">{rec.current_state}</div>
                          <div className="text-sm text-red-800 mb-1">{rec.recommendation}</div>
                          <div className="text-xs text-red-700">{rec.expected_impact}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* High-Impact Recommendations */}
              {(() => {
                const highImpactRecs = data.final_insights.prioritized_recommendations?.filter(
                  (rec: PrioritizedRecommendation) => rec.priority === 'High-Impact'
                ) || [];
                return highImpactRecs.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-orange-700 mb-2 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      Medium Impact
                    </h4>
                    <div className={spacing.itemGap}>
                      {highImpactRecs.map((rec: PrioritizedRecommendation, idx: number) => (
                        <div key={idx} className="p-3 bg-orange-50 rounded border border-orange-200">
                          <div className="font-semibold text-orange-900 text-sm mb-1">{rec.current_state}</div>
                          <div className="text-sm text-orange-800 mb-1">{rec.recommendation}</div>
                          <div className="text-xs text-orange-700">{rec.expected_impact}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Optimization Recommendations */}
              {(() => {
                const optimizationRecs = data.final_insights.prioritized_recommendations?.filter(
                  (rec: PrioritizedRecommendation) => rec.priority === 'Optimization'
                ) || [];
                return optimizationRecs.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      Low Impact
                    </h4>
                    <div className={spacing.itemGap}>
                      {optimizationRecs.map((rec: PrioritizedRecommendation, idx: number) => (
                        <div key={idx} className="p-3 bg-blue-50 rounded border border-blue-200">
                          <div className="font-semibold text-blue-900 text-sm mb-1">{rec.current_state}</div>
                          <div className="text-sm text-blue-800 mb-1">{rec.recommendation}</div>
                          <div className="text-xs text-blue-700">{rec.expected_impact}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Priority Areas - Full Width */}
          {data.final_insights.critical_actions && data.final_insights.critical_actions.length > 0 && (
            <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-orange-500 ${spacing.subsectionGap}`}>
              <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                Priority Areas of Focus
              </h3>
              <p className={`${typography.smallDescription} mb-3`}>Key observations that may require attention</p>
              <div className={spacing.itemGap}>
                {data.final_insights.critical_actions.map((action: string, idx: number) => (
                  <div key={idx} className={`flex items-start ${typography.body}`}>
                    <span className="mr-2 text-orange-600 font-bold flex-shrink-0">{idx + 1}.</span>
                    <div className="flex-1">
                      <MarkdownRenderer content={formatTextWithBoldPrefix(action)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detailed Analysis Section - Collapsible */}
      <div className={styling.section}>
        <button
          onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
          className={`w-full ${spacing.cardPadding} flex items-center justify-between hover:bg-gray-50 transition-colors`}
        >
          <div className="flex items-center flex-1">
            <Package className="w-5 h-5 text-gray-700 mr-3" />
            <div className="text-left flex-1">
              <div className="flex items-center gap-2">
                <h2 className={typography.subsectionTitle}>Detailed Analysis</h2>
                <span className={`px-2 py-1 bg-gray-100 text-gray-600 ${typography.smallDescription} font-medium rounded`}>
                  {[data.conversion_jobs_scorecard, data.winning_patterns_checklist, data.shelf_test_insights, data.competitive_intelligence, data.persona_reaction_insights, data.side_insights, data.back_insights].filter(Boolean).length} Sections
                </span>
              </div>
              <p className={`${typography.smallDescription} mt-1`}>
                {showDetailedAnalysis
                  ? `${data.conversion_jobs_scorecard ? 'Conversion jobs, ' : ''}${data.winning_patterns_checklist ? 'winning patterns, ' : ''}shelf performance${data.competitive_intelligence ? ', competitive intel' : ''}, front panel${data.side_insights ? ', side panel' : ''}${data.back_insights ? ', back panel' : ''}`
                  : 'Click to view in-depth analysis breakdowns'}
              </p>
            </div>
          </div>
          <div className={`transform transition-transform duration-200 ${showDetailedAnalysis ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {showDetailedAnalysis && (
          <div className={`${spacing.sectionPadding} pt-0 space-y-6 ${styling.divider}`}>

            {/* 1. Shopper Conversion Checklist */}
            {data.conversion_jobs_scorecard && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
                  <Award className="w-5 h-5 text-purple-600 mr-2" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Shopper Conversion Checklist</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      8 critical jobs packaging must accomplish to convert shoppers into buyers
                      {(data.side_insights || data.back_insights) && ' • Based on all packaging panels'}
                    </p>
                  </div>
                </div>

                {/* Overall Grade & Score - Compact Header */}
                <div className={`flex items-center justify-between ${spacing.subsectionGap}`}>
                  <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 rounded-lg ${
                      data.conversion_jobs_scorecard.overall_grade === 'A' ? 'bg-emerald-50 border-2 border-emerald-200' :
                      data.conversion_jobs_scorecard.overall_grade === 'B' ? 'bg-purple-50 border-2 border-purple-200' :
                      data.conversion_jobs_scorecard.overall_grade === 'C' ? 'bg-yellow-50 border-2 border-yellow-200' :
                      data.conversion_jobs_scorecard.overall_grade === 'D' ? 'bg-orange-50 border-2 border-orange-200' :
                      'bg-red-50 border-2 border-red-200'
                    }`}>
                      <span className={`text-3xl font-bold ${
                        data.conversion_jobs_scorecard.overall_grade === 'A' ? 'text-emerald-700' :
                        data.conversion_jobs_scorecard.overall_grade === 'B' ? 'text-purple-700' :
                        data.conversion_jobs_scorecard.overall_grade === 'C' ? 'text-yellow-700' :
                        data.conversion_jobs_scorecard.overall_grade === 'D' ? 'text-orange-700' :
                        'text-red-700'
                      }`}>{data.conversion_jobs_scorecard.overall_grade}</span>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">Overall Grade</div>
                      <div className={typography.smallDescription}>
                        {data.conversion_jobs_scorecard.overall_grade === 'A' ? 'Excellent - Ready to convert shoppers' :
                         data.conversion_jobs_scorecard.overall_grade === 'B' ? 'Good - Minor improvements needed' :
                         data.conversion_jobs_scorecard.overall_grade === 'C' ? 'Fair - Several areas need attention' :
                         data.conversion_jobs_scorecard.overall_grade === 'D' ? 'Needs Work - Significant gaps' :
                         'Poor - Major overhaul recommended'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Two-Column Layout: Radar Chart + Compact Score Grid */}
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
                  {/* Radar Chart */}
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} relative`}>
                    <h3 className={`${typography.cardTitle} mb-3 text-center`}>Performance Overview</h3>
                    {/* Label tooltip */}
                    {conversionJobsRadarTooltip && (
                      <div
                        className="absolute bg-gray-800 text-white text-xs px-2 py-1 rounded z-30 max-w-xs break-words pointer-events-none whitespace-nowrap"
                        style={{ top: `${conversionJobsRadarTooltip.y}px`, left: `${conversionJobsRadarTooltip.x}px`, transform: 'translate(-50%, 8px)' }}
                      >
                        {conversionJobsRadarTooltip.text}
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height={450}>
                      <RadarChart data={[
                        { job: 'Comprehension', score: data.conversion_jobs_scorecard.instant_comprehension?.score || 0, fullMark: 5 },
                        { job: 'Credibility', score: data.conversion_jobs_scorecard.proof_credibility?.score || 0, fullMark: 5 },
                        { job: 'Risk Reduction', score: data.conversion_jobs_scorecard.risk_reduction?.score || 0, fullMark: 5 },
                        { job: 'Usage Clarity', score: data.conversion_jobs_scorecard.usage_certainty?.score || 0, fullMark: 5 },
                        { job: 'Differentiation', score: data.conversion_jobs_scorecard.differentiation_positioning?.score || 0, fullMark: 5 },
                        { job: 'Value', score: data.conversion_jobs_scorecard.value_justification?.score || 0, fullMark: 5 },
                        { job: 'Identity', score: data.conversion_jobs_scorecard.identity_values_alignment?.score || 0, fullMark: 5 },
                        { job: 'Habit', score: data.conversion_jobs_scorecard.usage_occasion_habit?.score || 0, fullMark: 5 },
                      ]}>
                        <PolarGrid stroke="#E5E7EB" />
                        <PolarAngleAxis
                          dataKey="job"
                          tick={({ x, y, payload }: any) => {
                            const label = payload.value;
                            const maxLen = 14;
                            const isTruncated = typeof label === 'string' && label.length > maxLen;
                            const displayLabel = isTruncated ? label.slice(0, maxLen) + '...' : label;
                            return (
                              <g>
                                <foreignObject x={x - 50} y={y - 12} width={100} height={24}>
                                  <div
                                    onMouseEnter={() => isTruncated && setConversionJobsRadarTooltip({ text: label, x, y })}
                                    onMouseLeave={() => setConversionJobsRadarTooltip(null)}
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: '#1F2937',
                                      textAlign: 'center',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: isTruncated ? 'help' : 'default',
                                      textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white'
                                    }}
                                  >
                                    {displayLabel}
                                  </div>
                                </foreignObject>
                              </g>
                            );
                          }}
                        />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#9CA3AF', fontSize: 9 }} />
                        <Radar name="Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.5} />
                        <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Compact Score Grid */}
                  <div className={`${styling.nestedCard} ${spacing.cardPadding}`}>
                    <h3 className={`${typography.cardTitle} mb-3`}>Score Summary</h3>
                    <div className="space-y-2">
                      {[
                        { job: data.conversion_jobs_scorecard.instant_comprehension, label: 'Instant Comprehension' },
                        { job: data.conversion_jobs_scorecard.proof_credibility, label: 'Proof & Credibility' },
                        { job: data.conversion_jobs_scorecard.risk_reduction, label: 'Risk Reduction' },
                        { job: data.conversion_jobs_scorecard.usage_certainty, label: 'Usage Certainty' },
                        { job: data.conversion_jobs_scorecard.differentiation_positioning, label: 'Differentiation' },
                        { job: data.conversion_jobs_scorecard.value_justification, label: 'Value Justification' },
                        { job: data.conversion_jobs_scorecard.identity_values_alignment, label: 'Identity & Values' },
                        { job: data.conversion_jobs_scorecard.usage_occasion_habit, label: 'Usage & Habit' },
                      ]
                        .filter(item => item.job)
                        .sort((a, b) => (a.job?.score || 0) - (b.job?.score || 0))
                        .map((item, idx) => {
                        const score = item.job!.score;
                        return (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${
                                score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}></span>
                              <span className={typography.body}>{item.label}</span>
                            </div>
                            <div className={`font-semibold ${
                              score >= 4 ? 'text-green-600' : score >= 3 ? 'text-yellow-600' : 'text-red-600'
                            }`}>{score}/5</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Expand/Collapse Button for Details */}
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={() => setShowConversionJobsDetails(!showConversionJobsDetails)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                  >
                    <span className={typography.cardTitle}>View Detailed Job Analysis</span>
                    <ChevronDown className={`w-5 h-5 text-gray-600 transform transition-transform ${showConversionJobsDetails ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Expanded Details - Tiered Priority Grouping */}
                  {showConversionJobsDetails && (
                    <div className="mt-4 space-y-6">
                      {/* Needs Attention (0-2) */}
                      {(() => {
                        const needsAttention = [
                          data.conversion_jobs_scorecard.instant_comprehension,
                          data.conversion_jobs_scorecard.proof_credibility,
                          data.conversion_jobs_scorecard.risk_reduction,
                          data.conversion_jobs_scorecard.usage_certainty,
                          data.conversion_jobs_scorecard.differentiation_positioning,
                          data.conversion_jobs_scorecard.value_justification,
                          data.conversion_jobs_scorecard.identity_values_alignment,
                          data.conversion_jobs_scorecard.usage_occasion_habit,
                        ].filter(job => job && job.score <= 2);

                        return needsAttention.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle className="w-5 h-5 text-red-500" />
                              <h4 className="text-base font-semibold text-red-700">Needs Attention (Score 0-2)</h4>
                            </div>
                            <div className="space-y-3">
                              {needsAttention.map((job, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{job!.job_name}</h5>
                                      <p className={typography.smallDescription}>{jobDescriptions[job!.job_name] || job!.shopper_question}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-red-100 text-red-700 font-bold rounded">{job!.score}/5</div>
                                  </div>
                                  <div className={`${typography.body} mb-2`}>
                                    <MarkdownRenderer content={job!.score_rationale} variant="tiny" />
                                  </div>
                                  {job!.improvement_suggestions && job!.improvement_suggestions.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className={`${typography.smallTitle} text-red-700`}>Recommendations:</span>
                                      <div className="mt-1 space-y-1">
                                        {job!.improvement_suggestions.map((suggestion, sidx) => (
                                          <div key={sidx} className={`${typography.smallDescription} flex items-start gap-1`}>
                                            <span className="text-red-500 mt-0.5">•</span>
                                            <div className="flex-1"><MarkdownRenderer content={suggestion} variant="tiny" /></div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Moderate (3) */}
                      {(() => {
                        const moderate = [
                          data.conversion_jobs_scorecard.instant_comprehension,
                          data.conversion_jobs_scorecard.proof_credibility,
                          data.conversion_jobs_scorecard.risk_reduction,
                          data.conversion_jobs_scorecard.usage_certainty,
                          data.conversion_jobs_scorecard.differentiation_positioning,
                          data.conversion_jobs_scorecard.value_justification,
                          data.conversion_jobs_scorecard.identity_values_alignment,
                          data.conversion_jobs_scorecard.usage_occasion_habit,
                        ].filter(job => job && job.score === 3);

                        return moderate.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <TrendingUp className="w-5 h-5 text-yellow-500" />
                              <h4 className="text-base font-semibold text-yellow-700">Moderate (Score 3)</h4>
                            </div>
                            <div className="space-y-3">
                              {moderate.map((job, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-yellow-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{job!.job_name}</h5>
                                      <p className={typography.smallDescription}>{jobDescriptions[job!.job_name] || job!.shopper_question}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-yellow-100 text-yellow-700 font-bold rounded">{job!.score}/5</div>
                                  </div>
                                  <div className={`${typography.body} mb-2`}>
                                    <MarkdownRenderer content={job!.score_rationale} variant="tiny" />
                                  </div>
                                  {job!.improvement_suggestions && job!.improvement_suggestions.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className={`${typography.smallTitle} text-yellow-700`}>Recommendations:</span>
                                      <div className="mt-1 space-y-1">
                                        {job!.improvement_suggestions.map((suggestion, sidx) => (
                                          <div key={sidx} className={`${typography.smallDescription} flex items-start gap-1`}>
                                            <span className="text-yellow-500 mt-0.5">•</span>
                                            <div className="flex-1"><MarkdownRenderer content={suggestion} variant="tiny" /></div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Strong (4-5) */}
                      {(() => {
                        const strong = [
                          data.conversion_jobs_scorecard.instant_comprehension,
                          data.conversion_jobs_scorecard.proof_credibility,
                          data.conversion_jobs_scorecard.risk_reduction,
                          data.conversion_jobs_scorecard.usage_certainty,
                          data.conversion_jobs_scorecard.differentiation_positioning,
                          data.conversion_jobs_scorecard.value_justification,
                          data.conversion_jobs_scorecard.identity_values_alignment,
                          data.conversion_jobs_scorecard.usage_occasion_habit,
                        ].filter(job => job && job.score >= 4);

                        return strong.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <h4 className="text-base font-semibold text-green-700">Strong (Score 4-5)</h4>
                            </div>
                            <div className="space-y-3">
                              {strong.map((job, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{job!.job_name}</h5>
                                      <p className={typography.smallDescription}>{jobDescriptions[job!.job_name] || job!.shopper_question}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-green-100 text-green-700 font-bold rounded">{job!.score}/5</div>
                                  </div>
                                  <div className={`${typography.body} mb-2`}>
                                    <MarkdownRenderer content={job!.score_rationale} variant="tiny" />
                                  </div>
                                  {job!.evidence && job!.evidence.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className={`${typography.smallTitle} text-green-700`}>Evidence:</span>
                                      <div className="mt-1 space-y-1">
                                        {job!.evidence.map((ev, eidx) => (
                                          <div key={eidx} className={`${typography.smallDescription} flex items-start gap-1`}>
                                            <span className="text-green-500 mt-0.5">•</span>
                                            <div className="flex-1"><MarkdownRenderer content={ev} variant="tiny" /></div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. Winning Patterns Checklist */}
            {data.winning_patterns_checklist && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mr-2" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Winning Patterns Checklist</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Evaluation against 12 patterns used by top-performing CPG brands
                      {(data.side_insights || data.back_insights) && ' • Based on all packaging panels'}
                    </p>
                  </div>
                </div>

                {/* Overall Grade & Score - Compact Header */}
                <div className={`flex items-center justify-between ${spacing.subsectionGap}`}>
                  <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 rounded-lg ${
                      data.winning_patterns_checklist.overall_grade === 'A' ? 'bg-emerald-50 border-2 border-emerald-200' :
                      data.winning_patterns_checklist.overall_grade === 'B' ? 'bg-purple-50 border-2 border-purple-200' :
                      data.winning_patterns_checklist.overall_grade === 'C' ? 'bg-yellow-50 border-2 border-yellow-200' :
                      data.winning_patterns_checklist.overall_grade === 'D' ? 'bg-orange-50 border-2 border-orange-200' :
                      'bg-red-50 border-2 border-red-200'
                    }`}>
                      <span className={`text-3xl font-bold ${
                        data.winning_patterns_checklist.overall_grade === 'A' ? 'text-emerald-700' :
                        data.winning_patterns_checklist.overall_grade === 'B' ? 'text-purple-700' :
                        data.winning_patterns_checklist.overall_grade === 'C' ? 'text-yellow-700' :
                        data.winning_patterns_checklist.overall_grade === 'D' ? 'text-orange-700' :
                        'text-red-700'
                      }`}>{data.winning_patterns_checklist.overall_grade}</span>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">Overall Grade</div>
                      <div className={typography.smallDescription}>
                        {data.winning_patterns_checklist.overall_grade === 'A' ? 'Excellent - Using winning patterns effectively' :
                         data.winning_patterns_checklist.overall_grade === 'B' ? 'Good - Most patterns well implemented' :
                         data.winning_patterns_checklist.overall_grade === 'C' ? 'Fair - Key patterns missing or weak' :
                         data.winning_patterns_checklist.overall_grade === 'D' ? 'Needs Work - Many patterns absent' :
                         'Poor - Few winning patterns present'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Two-Column Layout: Radar Chart + Compact Pattern Grid */}
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
                  {/* Radar Chart */}
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} relative`}>
                    <h3 className={`${typography.cardTitle} mb-3 text-center`}>Effectiveness Overview</h3>
                    {/* Label tooltip */}
                    {winningPatternsRadarTooltip && (
                      <div
                        className="absolute bg-gray-800 text-white text-xs px-2 py-1 rounded z-30 max-w-xs break-words pointer-events-none whitespace-nowrap"
                        style={{ top: `${winningPatternsRadarTooltip.y}px`, left: `${winningPatternsRadarTooltip.x}px`, transform: 'translate(-50%, 8px)' }}
                      >
                        {winningPatternsRadarTooltip.text}
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height={450}>
                      <RadarChart data={[
                        { pattern: 'Bold Promise', score: data.winning_patterns_checklist.single_bold_promise?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Proof Cluster', score: data.winning_patterns_checklist.proof_cluster?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Benefit Bullets', score: data.winning_patterns_checklist.benefit_bullets?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Brand Story', score: data.winning_patterns_checklist.brand_story_block?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Ingredients', score: data.winning_patterns_checklist.ingredient_transparency?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Usage', score: data.winning_patterns_checklist.usage_instructions?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Risk Reversal', score: data.winning_patterns_checklist.risk_reversal?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Hierarchy', score: data.winning_patterns_checklist.visual_hierarchy?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Imagery', score: data.winning_patterns_checklist.emotional_imagery?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Must-Haves', score: data.winning_patterns_checklist.category_must_haves?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Interactive', score: data.winning_patterns_checklist.interactive_elements?.effectiveness || 0, fullMark: 5 },
                        { pattern: 'Cross-Sell', score: data.winning_patterns_checklist.cross_sell_cues?.effectiveness || 0, fullMark: 5 },
                      ]}>
                        <PolarGrid stroke="#E5E7EB" />
                        <PolarAngleAxis
                          dataKey="pattern"
                          tick={({ x, y, payload }: any) => {
                            const label = payload.value;
                            const maxLen = 12;
                            const isTruncated = typeof label === 'string' && label.length > maxLen;
                            const displayLabel = isTruncated ? label.slice(0, maxLen) + '...' : label;
                            return (
                              <g>
                                <foreignObject x={x - 50} y={y - 12} width={100} height={24}>
                                  <div
                                    onMouseEnter={() => isTruncated && setWinningPatternsRadarTooltip({ text: label, x, y })}
                                    onMouseLeave={() => setWinningPatternsRadarTooltip(null)}
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: '#1F2937',
                                      textAlign: 'center',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: isTruncated ? 'help' : 'default',
                                      textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white'
                                    }}
                                  >
                                    {displayLabel}
                                  </div>
                                </foreignObject>
                              </g>
                            );
                          }}
                        />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#9CA3AF', fontSize: 9 }} />
                        <Radar name="Effectiveness" dataKey="score" stroke="#6366F1" fill="#6366F1" fillOpacity={0.5} />
                        <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Compact Pattern Grid */}
                  <div className={`${styling.nestedCard} ${spacing.cardPadding}`}>
                    <h3 className={`${typography.cardTitle} mb-3`}>Pattern Summary</h3>
                    <div className="space-y-2">
                      {[
                        { pattern: data.winning_patterns_checklist.single_bold_promise, label: 'Single Bold Promise' },
                        { pattern: data.winning_patterns_checklist.proof_cluster, label: 'Proof Cluster' },
                        { pattern: data.winning_patterns_checklist.benefit_bullets, label: 'Benefit Bullets' },
                        { pattern: data.winning_patterns_checklist.brand_story_block, label: 'Brand Story Block' },
                        { pattern: data.winning_patterns_checklist.ingredient_transparency, label: 'Ingredient Transparency' },
                        { pattern: data.winning_patterns_checklist.usage_instructions, label: 'Usage Instructions' },
                        { pattern: data.winning_patterns_checklist.risk_reversal, label: 'Risk Reversal' },
                        { pattern: data.winning_patterns_checklist.visual_hierarchy, label: 'Visual Hierarchy' },
                        { pattern: data.winning_patterns_checklist.emotional_imagery, label: 'Emotional Imagery' },
                        { pattern: data.winning_patterns_checklist.category_must_haves, label: 'Category Must-Haves' },
                        { pattern: data.winning_patterns_checklist.interactive_elements, label: 'Interactive Elements' },
                        { pattern: data.winning_patterns_checklist.cross_sell_cues, label: 'Cross-Sell Cues' },
                      ]
                        .filter(item => item.pattern)
                        .sort((a, b) => {
                          // Missing patterns (not present) come first, then sort by effectiveness ascending
                          const aEffectiveness = a.pattern?.is_present ? (a.pattern?.effectiveness || 0) : -1;
                          const bEffectiveness = b.pattern?.is_present ? (b.pattern?.effectiveness || 0) : -1;
                          return aEffectiveness - bEffectiveness;
                        })
                        .map((item, idx) => {
                        const isPresent = item.pattern!.is_present;
                        const effectiveness = item.pattern!.effectiveness;
                        return (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center gap-2">
                              {isPresent ? (
                                <CheckCircle className={`w-4 h-4 ${
                                  effectiveness >= 4 ? 'text-green-500' : effectiveness >= 3 ? 'text-yellow-500' : 'text-red-500'
                                }`} />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-gray-400" />
                              )}
                              <span className={typography.body}>{item.label}</span>
                            </div>
                            {isPresent ? (
                              <div className={`font-semibold ${
                                effectiveness >= 4 ? 'text-green-600' : effectiveness >= 3 ? 'text-yellow-600' : 'text-red-600'
                              }`}>{effectiveness}/5</div>
                            ) : (
                              <span className="text-xs text-gray-400">Missing</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Expand/Collapse Button for Details */}
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={() => setShowWinningPatternsDetails(!showWinningPatternsDetails)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                  >
                    <span className={typography.cardTitle}>View Detailed Pattern Analysis</span>
                    <ChevronDown className={`w-5 h-5 text-gray-600 transform transition-transform ${showWinningPatternsDetails ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Expanded Details - Tiered Priority Grouping */}
                  {showWinningPatternsDetails && (
                    <div className="mt-4 space-y-6">
                      {/* Not Present (Missing Patterns) */}
                      {(() => {
                        const notPresent = [
                          data.winning_patterns_checklist.single_bold_promise,
                          data.winning_patterns_checklist.proof_cluster,
                          data.winning_patterns_checklist.benefit_bullets,
                          data.winning_patterns_checklist.brand_story_block,
                          data.winning_patterns_checklist.ingredient_transparency,
                          data.winning_patterns_checklist.usage_instructions,
                          data.winning_patterns_checklist.risk_reversal,
                          data.winning_patterns_checklist.visual_hierarchy,
                          data.winning_patterns_checklist.emotional_imagery,
                          data.winning_patterns_checklist.category_must_haves,
                          data.winning_patterns_checklist.interactive_elements,
                          data.winning_patterns_checklist.cross_sell_cues,
                        ].filter(pattern => pattern && !pattern.is_present);

                        return notPresent.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle className="w-5 h-5 text-gray-500" />
                              <h4 className="text-base font-semibold text-gray-700">Missing Patterns</h4>
                            </div>
                            <div className="space-y-3">
                              {notPresent.map((pattern, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-gray-400`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{pattern!.pattern_name}</h5>
                                      <p className={typography.smallDescription}>{patternDescriptions[pattern!.pattern_name] || pattern!.what_winners_do}</p>
                                    </div>
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">Not Present</span>
                                  </div>
                                  <div className={`${typography.smallDescription} mb-2`}>
                                    <MarkdownRenderer content={pattern!.evidence} variant="tiny" />
                                  </div>
                                  <div className="mt-2 pt-2 border-t border-gray-100">
                                    <span className={`${typography.smallTitle} text-orange-700`}>Implementation Recommendation:</span>
                                    <div className={typography.smallDescription}>
                                      <MarkdownRenderer content={pattern!.recommendation} variant="tiny" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Needs Improvement (Present but 0-2) */}
                      {(() => {
                        const needsImprovement = [
                          data.winning_patterns_checklist.single_bold_promise,
                          data.winning_patterns_checklist.proof_cluster,
                          data.winning_patterns_checklist.benefit_bullets,
                          data.winning_patterns_checklist.brand_story_block,
                          data.winning_patterns_checklist.ingredient_transparency,
                          data.winning_patterns_checklist.usage_instructions,
                          data.winning_patterns_checklist.risk_reversal,
                          data.winning_patterns_checklist.visual_hierarchy,
                          data.winning_patterns_checklist.emotional_imagery,
                          data.winning_patterns_checklist.category_must_haves,
                          data.winning_patterns_checklist.interactive_elements,
                          data.winning_patterns_checklist.cross_sell_cues,
                        ].filter(pattern => pattern && pattern.is_present && pattern.effectiveness <= 2);

                        return needsImprovement.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle className="w-5 h-5 text-red-500" />
                              <h4 className="text-base font-semibold text-red-700">Needs Improvement (Score 0-2)</h4>
                            </div>
                            <div className="space-y-3">
                              {needsImprovement.map((pattern, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{pattern!.pattern_name}</h5>
                                      <p className={typography.smallDescription}>{patternDescriptions[pattern!.pattern_name] || pattern!.what_winners_do}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-red-100 text-red-700 font-bold rounded">{pattern!.effectiveness}/5</div>
                                  </div>
                                  <div className={`${typography.smallDescription} mb-2`}>
                                    <MarkdownRenderer content={pattern!.evidence} variant="tiny" />
                                  </div>
                                  <div className="mt-2 pt-2 border-t border-gray-100">
                                    <span className={`${typography.smallTitle} text-red-700`}>Enhancement Recommendation:</span>
                                    <div className={typography.smallDescription}>
                                      <MarkdownRenderer content={pattern!.recommendation} variant="tiny" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Moderate (Present with 3) */}
                      {(() => {
                        const moderate = [
                          data.winning_patterns_checklist.single_bold_promise,
                          data.winning_patterns_checklist.proof_cluster,
                          data.winning_patterns_checklist.benefit_bullets,
                          data.winning_patterns_checklist.brand_story_block,
                          data.winning_patterns_checklist.ingredient_transparency,
                          data.winning_patterns_checklist.usage_instructions,
                          data.winning_patterns_checklist.risk_reversal,
                          data.winning_patterns_checklist.visual_hierarchy,
                          data.winning_patterns_checklist.emotional_imagery,
                          data.winning_patterns_checklist.category_must_haves,
                          data.winning_patterns_checklist.interactive_elements,
                          data.winning_patterns_checklist.cross_sell_cues,
                        ].filter(pattern => pattern && pattern.is_present && pattern.effectiveness === 3);

                        return moderate.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <TrendingUp className="w-5 h-5 text-yellow-500" />
                              <h4 className="text-base font-semibold text-yellow-700">Moderate (Score 3)</h4>
                            </div>
                            <div className="space-y-3">
                              {moderate.map((pattern, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-yellow-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{pattern!.pattern_name}</h5>
                                      <p className={typography.smallDescription}>{patternDescriptions[pattern!.pattern_name] || pattern!.what_winners_do}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-yellow-100 text-yellow-700 font-bold rounded">{pattern!.effectiveness}/5</div>
                                  </div>
                                  <div className={`${typography.smallDescription} mb-2`}>
                                    <MarkdownRenderer content={pattern!.evidence} variant="tiny" />
                                  </div>
                                  <div className="mt-2 pt-2 border-t border-gray-100">
                                    <span className={`${typography.smallTitle} text-yellow-700`}>Enhancement Recommendation:</span>
                                    <div className={typography.smallDescription}>
                                      <MarkdownRenderer content={pattern!.recommendation} variant="tiny" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Strong (Present with 4-5) */}
                      {(() => {
                        const strong = [
                          data.winning_patterns_checklist.single_bold_promise,
                          data.winning_patterns_checklist.proof_cluster,
                          data.winning_patterns_checklist.benefit_bullets,
                          data.winning_patterns_checklist.brand_story_block,
                          data.winning_patterns_checklist.ingredient_transparency,
                          data.winning_patterns_checklist.usage_instructions,
                          data.winning_patterns_checklist.risk_reversal,
                          data.winning_patterns_checklist.visual_hierarchy,
                          data.winning_patterns_checklist.emotional_imagery,
                          data.winning_patterns_checklist.category_must_haves,
                          data.winning_patterns_checklist.interactive_elements,
                          data.winning_patterns_checklist.cross_sell_cues,
                        ].filter(pattern => pattern && pattern.is_present && pattern.effectiveness >= 4);

                        return strong.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <h4 className="text-base font-semibold text-green-700">Strong (Score 4-5)</h4>
                            </div>
                            <div className="space-y-3">
                              {strong.map((pattern, idx) => (
                                <div key={idx} className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h5 className={typography.cardTitle}>{pattern!.pattern_name}</h5>
                                      <p className={typography.smallDescription}>{patternDescriptions[pattern!.pattern_name] || pattern!.what_winners_do}</p>
                                    </div>
                                    <div className="px-3 py-1 bg-green-100 text-green-700 font-bold rounded">{pattern!.effectiveness}/5</div>
                                  </div>
                                  <div className={`${typography.smallDescription} mb-2`}>
                                    <MarkdownRenderer content={pattern!.evidence} variant="tiny" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 3. Technical Compliance Insights */}
            {/* {data.technical_compliance_insights && (
        <div className={`${styling.section} ${spacing.sectionPadding}`}>
          <div className={`flex items-center mb-4 pb-4 ${styling.divider}`}>
            <Package className="w-5 h-5 text-purple-600 mr-2" />
            <div>
              <h2 className={typography.sectionTitle}>Technical Analysis</h2>
              {(data.side_insights || data.back_insights) && (
                <p className="text-xs text-gray-500 mt-1">Based on all packaging panels</p>
              )}
            </div>
          </div> */}

          {/* Scores - Visual Cards with Explanations */}
          {/* <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
            {data.technical_compliance_insights.overall_readability_score !== undefined && (
              <div>
                {renderScoreCard(
                  data.technical_compliance_insights.overall_readability_score.score, 
                'Readability Score',
                <Eye className="w-5 h-5" />
              )}
                {data.technical_compliance_insights.overall_readability_score.reason && (
                  <div className={`mt-2 ${spacing.smallPadding} ${
                    data.technical_compliance_insights.overall_readability_score.score >= 8 ? 'bg-green-50 border-green-200' :
                    data.technical_compliance_insights.overall_readability_score.score >= 6 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  } rounded border-2`}>
                    <p className={`${typography.smallDescription} leading-relaxed`}>
                      {data.technical_compliance_insights.overall_readability_score.reason}
                    </p>
                  </div>
                )}
              </div>
            )}
            {data.technical_compliance_insights.overall_hierarchy_score !== undefined && (
              <div>
                {renderScoreCard(
                  data.technical_compliance_insights.overall_hierarchy_score.score, 
                'Information Hierarchy Score',
                <Package className="w-5 h-5" />
                )}
                {data.technical_compliance_insights.overall_hierarchy_score.reason && (
                  <div className={`mt-2 ${spacing.smallPadding} ${
                    data.technical_compliance_insights.overall_hierarchy_score.score >= 8 ? 'bg-green-50 border-green-200' :
                    data.technical_compliance_insights.overall_hierarchy_score.score >= 6 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  } rounded border-2`}>
                    <p className={`${typography.smallDescription} leading-relaxed`}>
                      {data.technical_compliance_insights.overall_hierarchy_score.reason}
                    </p>
                  </div>
                )}
              </div>
              )}
          </div> */}

          {/* COMMENTED OUT - Compliance Status 
          {data.technical_compliance_insights.compliance_status && (
            <div className={`mb-4 ${spacing.cardPadding} rounded border-l-4 ${
              data.technical_compliance_insights.compliance_status.toLowerCase().includes('major') 
                ? `${styling.nestedCard} border-l-red-500` 
                : `${styling.nestedCard} border-l-green-500`
            }`}>
              <h3 className={typography.smallTitle}>
                Compliance Status: {data.technical_compliance_insights.compliance_status}
              </h3>
            </div>
          )}
          */}

          {/* Key Strengths & Areas to Focus - Side by Side */}
          {/* <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}> */}
            {/* Key Strengths */}
            {/* {data.technical_compliance_insights.key_strengths && data.technical_compliance_insights.key_strengths.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-green-500`}>
                <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Key Strengths
                </h3>
                <p className={`${typography.smallDescription} mb-3`}>Positive attributes observed in the packaging design</p>
                <div className={spacing.itemGap}>
                  {data.technical_compliance_insights.key_strengths.map((strength: string, idx: number) => (
                    <div key={idx} className={`flex items-baseline ${typography.body}`}>
                      <span className="mr-2 text-green-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(strength)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Areas to Focus */}
            {/* {data.technical_compliance_insights.all_risk_flags && data.technical_compliance_insights.all_risk_flags.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-orange-500`}>
                <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                  <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                  Areas to Focus
                </h3>
                <p className={`${typography.smallDescription} mb-3`}>Potential compliance or regulatory considerations identified</p>
                <div className={spacing.itemGap}>
                  {data.technical_compliance_insights.all_risk_flags.map((flag: string, idx: number) => (
                    <div key={idx} className={`flex items-baseline ${typography.body}`}>
                      <span className="mr-2 text-orange-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(flag)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}
          {/* </div> */}

          {/* COMMENTED OUT - Technical Analysis Table - Dropdown
          {data.technical_and_compliance_analysis?.technical_and_compliance_analysis_outputs && 
           data.technical_and_compliance_analysis.technical_and_compliance_analysis_outputs.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowTechnicalTable(!showTechnicalTable)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">View Detailed Technical Analysis by Image</span>
                <svg 
                  className={`w-4 h-4 text-gray-600 transform transition-transform ${showTechnicalTable ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showTechnicalTable && (
                <div className="mt-3">
                  {(() => {
                    const tableData = convertToTableData(
                      data.technical_and_compliance_analysis.technical_and_compliance_analysis_outputs,
                      'Technical Analysis per Image'
                    );
                    return tableData ? (
                      <CollapsibleTable
                        table={tableData}
                        isExporting={isPreparingExport}
                        showFullContent={true}
                        showExportButtons={true}
                        hideTitle={true}
                        images={data.packaging_images || []}
                      />
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}
          */}
        {/* </div> */}
      {/* )} */}

      {/* 4. Shelf Test Analysis */}
      {data.shelf_test_insights && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
            <Eye className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Shelf Test Analysis</h2>
              <p className="text-xs text-gray-500 mt-1">Based on front panel</p>
            </div>
          </div>

          {/* AI-Generated Shelf Attention Heatmap */}
          {data.shelf_image && (
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded border-l-4 border-l-indigo-500">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Eye className="w-4 h-4 mr-2 text-indigo-600" />
                Shelf Attention Heatmap
              </h3>
              <div className="space-y-3">
                <div className="flex justify-center bg-white rounded-lg p-4">
                  <img 
                    src={buildImageUrl(data.shelf_image)} 
                    alt="Shelf Attention Heatmap" 
                    className="max-w-full h-auto rounded-lg shadow-lg border-2 border-indigo-300"
                  />
                </div>
                <div className="text-xs text-gray-700 text-center bg-white/70 p-3 rounded-lg">
                  <strong>Visual representation</strong> showing which products caught personas' attention first on the shelf
                </div>
              </div>
            </div>
          )}

          {/* Brand/Product First Attention Bar Chart */}
          {(() => {
            // Debug logging
            console.log("Checking product_shelf_view data:", {
              exists: !!data.shelf_test_insights?.product_shelf_view,
              length: data.shelf_test_insights?.product_shelf_view?.length || 0,
              data: data.shelf_test_insights?.product_shelf_view
            });
            
            const products = data.shelf_test_insights?.product_shelf_view || [];
            const filteredProducts = products.filter((p: any) => p.first_view_percent > 0);
            
            console.log("Filtered products:", filteredProducts);
            
            if (products.length === 0) {
              return null;
            }
            
            return (
              <div className={`${spacing.subsectionGap} ${spacing.cardPadding} ${styling.nestedCard}`}>
                <p className={`${typography.description} mb-4`}>Percentage of respondents who noticed each product first on the shelf</p>
                {filteredProducts.length > 0 ? (
                  <BarChart
                    data={filteredProducts.sort((a: any, b: any) => b.first_view_percent - a.first_view_percent)}
                    xAxis="product_name"
                    yAxis="first_view_percent"
                    title="Which Brand/Product Caught Your Attention First?"
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No products with attention data available
                  </div>
                )}
              </div>
            );
          })()}

          {/* First Attention Themes Bar Chart */}
          {data.shelf_test_insights.first_attention_themes && data.shelf_test_insights.first_attention_themes.length > 0 && (
            <div className={`${spacing.subsectionGap} ${spacing.cardPadding} ${styling.nestedCard}`}>
              <BarChart 
                data={data.shelf_test_insights.first_attention_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Cumulative Factors That Draw Initial Attention"
              />
            </div>
          )}

          {/* Key Strengths & Areas to Focus - Side by Side */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
            {data.shelf_test_insights.competitive_advantages && data.shelf_test_insights.competitive_advantages.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-green-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Shelf Strengths
                </h3>
                {renderList(data.shelf_test_insights.competitive_advantages)}
              </div>
            )}

            {data.shelf_test_insights.competitive_disadvantages && data.shelf_test_insights.competitive_disadvantages.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-orange-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                  Shelf Weaknesses
                </h3>
                {renderList(data.shelf_test_insights.competitive_disadvantages)}
              </div>
            )}
          </div>
          
          {/* Shelf Test Table - Dropdown */}
          {/* {data.shelf_test_responses?.persona_responses && 
           data.shelf_test_responses.persona_responses.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowShelfTable(!showShelfTable)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">View Detailed Shelf Test Responses by Persona</span>
                <svg 
                  className={`w-4 h-4 text-gray-600 transform transition-transform ${showShelfTable ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showShelfTable && (
                <div className="mt-3">
                  {(() => {
                    const tableData = convertToTableData(
                      data.shelf_test_responses.persona_responses,
                      'Shelf Test Responses by Persona'
                    );
                    return tableData ? (
                      <CollapsibleTable
                        table={tableData}
                        isExporting={isPreparingExport}
                        showFullContent={true}
                        showExportButtons={true}
                        hideTitle={true}
                      />
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )} */}
        </div>
      )}

      {/* Competitive Intelligence */}
      {data.competitive_intelligence && (() => {
        const ci = data.competitive_intelligence;
        if (!ci.target_product_name?.trim() || !ci.dimension_scores?.length) return null;
        const targetNameLower = ci.target_product_name.toLowerCase();

        // Word-boundary fuzzy match (avoids "Apple Juice" matching "Pineapple Juice")
        const matchesTarget = (productName: string) => {
          if (productName === ci.target_product_name) return true;
          const nameLower = productName.toLowerCase();
          return nameLower === targetNameLower
            || nameLower.startsWith(targetNameLower + ' ')
            || nameLower.startsWith(targetNameLower + '(')
            || targetNameLower.startsWith(nameLower + ' ')
            || targetNameLower.startsWith(nameLower + '(');
        };

        // Find target product in a score list — exact first, then fuzzy
        const findTargetProduct = (scores: ProductDimensionScore[]) =>
          scores.find(ps => ps.product_name === ci.target_product_name)
          || scores.find(ps => matchesTarget(ps.product_name));

        // Check if a product is the target — skip fuzzy if exact match exists in the set
        const isTargetProduct = (productName: string, hasExactMatch: boolean) =>
          hasExactMatch ? productName === ci.target_product_name : matchesTarget(productName);

        const DIMENSIONS = [
          { key: 'color', name: 'COLOR & CONTRAST', scaleLow: 'Blends In', scaleHigh: 'Stands Out' },
          { key: 'shape_layout', name: 'SHAPES & LAYOUT', scaleLow: 'Cluttered', scaleHigh: 'Clean' },
          { key: 'symbols_branding', name: 'SYMBOLS & BRANDING', scaleLow: 'Generic', scaleHigh: 'Recognizable' },
          { key: 'words_claims', name: 'CLAIMS & WORDS', scaleLow: 'Weak', scaleHigh: 'Clear' },
        ];

        return (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-8">
            {/* Section Header */}
            <div className="flex items-center pb-4 border-b border-gray-200">
              <Zap className="w-5 h-5 text-indigo-600 mr-2" />
              <div>
                <h2 className={typography.sectionTitle}>Competitive Analysis</h2>
                <p className={typography.smallDescription + ' mt-1'}>
                  How {ci.target_product_name} compares with key competitors
                </p>
              </div>
            </div>

            {/* ===== SECTION 1: Category Scorecard (Dot-Plot) ===== */}
            <div>
              <div className="space-y-3">
                {DIMENSIONS.map((dim) => {
                  const dimResult = ci.dimension_scores.find(d => d.dimension_key === dim.key);
                  if (!dimResult) return null;
                  const targetScore = findTargetProduct(dimResult.product_scores);
                  const bestScore = Math.max(...dimResult.product_scores.map(ps => ps.score));

                  return (
                    <div key={dim.key}>
                      {/* Row: Label | Scale | Score */}
                      <div className="flex items-center gap-3">
                        {/* Dimension label */}
                        <div className="w-20 flex-shrink-0">
                          <span className="text-xs font-semibold text-gray-900">{dim.name}</span>
                        </div>

                        {/* Scale labels + track */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-20 text-right flex-shrink-0">{dim.scaleLow}</span>
                            <div className="flex-1 relative h-16 bg-gray-50 rounded border border-gray-200">
                              {/* Product dots with vertical jitter for overlaps */}
                              {(() => {
                                const sorted = [...dimResult.product_scores].sort((a, b) => a.score - b.score);
                                // Compute vertical offsets: stack dots that are within 0.5 of each other
                                const offsets: number[] = [];
                                for (let i = 0; i < sorted.length; i++) {
                                  let jitterIdx = 0;
                                  for (let j = 0; j < i; j++) {
                                    if (Math.abs(sorted[i].score - sorted[j].score) < 0.5) {
                                      jitterIdx++;
                                    }
                                  }
                                  offsets.push(jitterIdx);
                                }
                                const maxJitter = Math.max(...offsets, 0);
                                const trackHeight = 64;
                                const dotSize = 8;
                                const padding = 6; // min padding from top/bottom edges
                                const usableHeight = trackHeight - dotSize - padding * 2;
                                // Adapt step to fit all dots within usable area
                                const jitterStep = maxJitter > 0 ? Math.min(14, usableHeight / maxJitter) : 14;
                                const hasExactMatch = sorted.some(s => s.product_name === ci.target_product_name);
                                return sorted.map((ps, idx) => {
                                  const isTarget = isTargetProduct(ps.product_name, hasExactMatch);
                                  const isBest = ps.score === bestScore && !isTarget;
                                  const leftPct = Math.max(3, Math.min(97, (ps.score / 10) * 100));
                                  // Center the jitter group vertically within track
                                  const totalHeight = maxJitter * jitterStep;
                                  const topPx = padding + (usableHeight - totalHeight) / 2 + offsets[idx] * jitterStep;
                                  return (
                                    <div key={idx} className="absolute group cursor-pointer" style={{ left: `${leftPct}%`, top: `${topPx}px` }}>
                                      <div className={`rounded-full border transition-transform group-hover:scale-150 ${
                                        isTarget
                                          ? 'w-2.5 h-2.5 bg-indigo-600 border-indigo-600'
                                          : isBest
                                            ? 'w-2 h-2 bg-amber-400 border-amber-500'
                                            : 'w-2 h-2 bg-white border-gray-400'
                                      }`} />
                                      <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 z-20 shadow-lg w-max max-w-[280px]">
                                        <div className="font-semibold whitespace-normal">{ps.product_name}: {Math.round(ps.score)}/10</div>
                                        <div className="text-gray-300 mt-0.5 whitespace-normal">{ps.rationale}</div>
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                            <span className="text-xs text-gray-400 w-20 flex-shrink-0">{dim.scaleHigh}</span>
                          </div>
                        </div>

                        {/* Target score */}
                        <span className={`text-sm font-semibold w-10 text-right flex-shrink-0 ${
                          targetScore?.score != null ? (targetScore.score >= 7 ? 'text-emerald-600' : targetScore.score >= 4 ? 'text-amber-600' : 'text-red-600') : 'text-gray-400'
                        }`}>
                          {targetScore?.score != null ? `${Math.round(targetScore.score)}/10` : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
                  <span>You</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span>Leader</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-white border border-gray-400" />
                  <span>Others</span>
                </div>
              </div>

              {/* Competitive Takeaway */}
              {ci.competitive_takeaway && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className={typography.smallTitle + ' mb-3'}>Competitive Takeaway</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 py-2 border-b border-gray-100">
                      <span className="w-1 self-stretch bg-red-400 rounded-full flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-gray-800">Competitive Threats</span>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{ci.competitive_takeaway.competitive_threats}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 py-2 border-b border-gray-100">
                      <span className="w-1 self-stretch bg-emerald-400 rounded-full flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-gray-800">Your Advantages</span>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{ci.competitive_takeaway.our_advantages}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 py-2 border-b border-gray-100">
                      <span className="w-1 self-stretch bg-amber-400 rounded-full flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-gray-800">Borrowable Equities</span>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{ci.competitive_takeaway.borrowable_equities}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 py-2">
                      <span className="w-1 self-stretch bg-indigo-400 rounded-full flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-gray-800">The Tradeoff</span>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{ci.competitive_takeaway.key_tension}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 5. Front Panel Analysis */}
      {data.persona_reaction_insights && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
            <Users className="w-5 h-5 text-orange-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {'Front Analysis'}
              </h2>
              <p className="text-xs text-gray-500 mt-1">Consumer reactions to the front view of the packaging</p>
            </div>
          </div>

          {/* Quantitative Scores - Visual with Chart */}
          {data.persona_reaction_insights.quantitative_summary && data.persona_reaction_insights.quantitative_summary.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                Performance Scores
              </h3>

              <div className="bg-white rounded border border-gray-200 p-4 relative">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">Performance Radar</h4>
                {/* Label tooltip */}
                {radarTooltip && (
                  <div
                    className="absolute bg-gray-800 text-white text-xs px-2 py-1 rounded z-30 max-w-xs break-words pointer-events-none whitespace-nowrap"
                    style={{ top: `${radarTooltip.y}px`, left: `${radarTooltip.x}px`, transform: 'translate(-50%, 8px)' }}
                  >
                    {radarTooltip.text}
                  </div>
                )}
                <ResponsiveContainer width="100%" height={450}>
                  <RadarChart data={data.persona_reaction_insights.quantitative_summary.map((m: any) => ({
                    dimension: getDimensionDisplayName(m.dimension),
                    score: m.mean || 0
                  }))}>
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={({ x, y, payload }: any) => {
                        const label = payload.value;
                        const maxLen = 15;
                        const isTruncated = typeof label === 'string' && label.length > maxLen;
                        const displayLabel = isTruncated ? label.slice(0, maxLen) + '...' : label;
                        return (
                          <g>
                            <foreignObject x={x - 65} y={y - 12} width={130} height={24}>
                              <div
                                onMouseEnter={() => isTruncated && setRadarTooltip({ text: label, x, y })}
                                onMouseLeave={() => setRadarTooltip(null)}
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#1F2937',
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: isTruncated ? 'help' : 'default',
                                  textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white'
                                }}
                              >
                                {displayLabel}
                              </div>
                            </foreignObject>
                          </g>
                        );
                      }}
                    />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#03e8d3"
                      fill="#03e8d3"
                      fillOpacity={0.5}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* NEW SECTION - Themed Question Bar Charts */}
          
          {/* Question 1: First Notice */}
          {/* {data.persona_reaction_insights.first_notice_themes && data.persona_reaction_insights.first_notice_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Open-ended response about visual elements, text, colors, shapes, design features</p>
              <BarChart 
                data={data.persona_reaction_insights.first_notice_themes}
                xAxis="theme"
                yAxis="percentage"
                title="What is the first thing you notice when you look at this packaging?"
              />
            </div>
          )} */}

          {/* Question 2: Like Factors */}
          {data.persona_reaction_insights.like_factors_themes && data.persona_reaction_insights.like_factors_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Positive features and attributes that resonate</p>
              <BarChart 
                data={data.persona_reaction_insights.like_factors_themes}
                xAxis="theme"
                yAxis="percentage"
                title="What do you like about this packaging?"
              />
            </div>
          )}

          {/* Question 3: Dislike Factors */}
          {data.persona_reaction_insights.dislike_factors_themes && data.persona_reaction_insights.dislike_factors_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Negative features, concerns, or areas of criticism</p>
              <BarChart 
                data={data.persona_reaction_insights.dislike_factors_themes}
                xAxis="theme"
                yAxis="percentage"
                title="What do you dislike about this packaging?"
              />
            </div>
          )}

          {/* Question 4: Emotional Words */}
          {data.persona_reaction_insights.emotional_words_themes && data.persona_reaction_insights.emotional_words_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Emotional responses and feelings evoked (3-5 words per persona)</p>
              <BarChart 
                data={data.persona_reaction_insights.emotional_words_themes}
                xAxis="theme"
                yAxis="percentage"
                title="What emotional words describe how you feel when looking at this packaging?"
              />
            </div>
          )}

          {/* Question 5: Perceived Promise */}
          {data.persona_reaction_insights.perceived_promise_themes && data.persona_reaction_insights.perceived_promise_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Perceived benefits, brand promises, product expectations</p>
              <BarChart 
                data={data.persona_reaction_insights.perceived_promise_themes}
                xAxis="theme"
                yAxis="percentage"
                title="What promise do you think this packaging is making to you?"
              />
            </div>
          )}

          {/* Attention Map Summary */}
          {/* {data.persona_reaction_insights.attention_map_summary && data.persona_reaction_insights.attention_map_summary.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Packaging elements that caught personas' attention, ranked by mention frequency</p>
              <BarChart
                data={data.persona_reaction_insights.attention_map_summary
                  .map((item: any) => ({
                    element: item.element,
                    share_of_mention: item.share_of_mention || 0
                  }))
                  .sort((a: any, b: any) => b.share_of_mention - a.share_of_mention)}
                xAxis="element"
                yAxis="share_of_mention"
                title="Which packaging elements caught your attention?"
              />
            </div>
          )} */}

          {/* Usage Occasion Themes */}
          {data.persona_reaction_insights.usage_occasion_themes && data.persona_reaction_insights.usage_occasion_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Common usage contexts and occasions personas imagine for this product</p>
              <BarChart
                data={data.persona_reaction_insights.usage_occasion_themes}
                xAxis="theme"
                yAxis="percentage"
                title="When and where would you use this product?"
              />
            </div>
          )}

          {/* Emotional Clusters - Simplified */}
          {/* {data.persona_reaction_insights.emotional_clusters && data.persona_reaction_insights.emotional_clusters.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                <Users className="w-5 h-5 mr-2 text-purple-600" />
                Emotional Response Themes
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Common emotional themes expressed by personas when viewing the packaging
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.persona_reaction_insights.emotional_clusters.map((cluster: any, idx: number) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded p-4">
                    <div className="mb-3">
                      <h4 className="font-semibold text-gray-900 text-base mb-1">{cluster.cluster_name}</h4>
                      <p className="text-sm text-purple-600 font-medium">
                        Mentioned by {cluster.share_of_voice?.toFixed(0)}% of respondents
                      </p>
                    </div>

                    {cluster.words && cluster.words.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {cluster.words.map((word: string, widx: number) => (
                          <span 
                            key={widx} 
                            className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full border border-purple-200"
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Key Strengths & Areas to Focus - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {data.persona_reaction_insights.deal_makers && data.persona_reaction_insights.deal_makers.length > 0 && (
              <div className="bg-white rounded border border-gray-200 p-4 border-l-4 border-l-green-500">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Purchase Drivers
                </h3>
                <p className="text-xs text-gray-500 mb-3">Factors that would make personas more likely to purchase</p>
                <div className="space-y-2">
                  {data.persona_reaction_insights.deal_makers.map((item: string, idx: number) => (
                    <div key={idx} className="flex items-baseline text-sm text-gray-700">
                      <span className="mr-2 text-green-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(item)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.persona_reaction_insights.deal_breakers && data.persona_reaction_insights.deal_breakers.length > 0 && (
              <div className="bg-white rounded border border-gray-200 p-4 border-l-4 border-l-red-500">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                  Purchase Barriers
                </h3>
                <p className="text-xs text-gray-500 mb-3">Factors that would make personas less likely to purchase</p>
                <div className="space-y-2">
                  {data.persona_reaction_insights.deal_breakers.map((item: string, idx: number) => (
                    <div key={idx} className="flex items-baseline text-sm text-gray-700">
                      <span className="mr-2 text-red-600 font-bold flex-shrink-0">{idx + 1}.</span>
                      <div className="flex-1">
                        <MarkdownRenderer content={formatTextWithBoldPrefix(item)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Packaging Element Heatmaps */}
          {/* {(data.packaging_like_heatmaps && data.packaging_like_heatmaps.length > 0) ||
           (data.packaging_dislike_heatmaps && data.packaging_dislike_heatmaps.length > 0) ? (
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                <Eye className="w-5 h-5 mr-2 text-indigo-600" />
                Packaging Element Attention
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Visual heatmaps showing which packaging elements personas focused on when expressing likes and dislikes. Color intensity shows frequency of mentions.
              </p>

              {/* Side-by-side grid for each packaging image */}
              {/* {Array.from({ length: Math.max(
                data.packaging_like_heatmaps?.length || 0,
                data.packaging_dislike_heatmaps?.length || 0
              )}).map((_, idx) => (
                <div key={idx} className="mb-6 last:mb-0">
                  <h4 className="font-semibold text-gray-900 mb-3 text-base">Packaging Image</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Like Heatmap */}
                    {/* {data.packaging_like_heatmaps && data.packaging_like_heatmaps[idx] && (
                      <div className="bg-white rounded border border-gray-200 p-4 border-l-4 border-l-green-500">
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                          Elements Personas Liked
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">Colored areas highlight elements that personas mentioned positively</p>
                        <div className="border border-gray-200 rounded p-2 bg-gray-50">
                          <img
                            src={data.packaging_like_heatmaps[idx]}
                            alt={`Packaging ${idx + 1} - Like Heatmap`}
                            className="w-full h-auto rounded"
                          />
                        </div>
                      </div>
                    )} */}

                    {/* Dislike Heatmap */}
                    {/* {data.packaging_dislike_heatmaps && data.packaging_dislike_heatmaps[idx] && (
                      <div className="bg-white rounded border border-gray-200 p-4 border-l-4 border-l-orange-500">
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                          <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                          Elements Personas Disliked
                        </h5>
                        <p className="text-xs text-gray-500 mb-3">Colored areas highlight elements that personas mentioned negatively</p>
                        <div className="border border-gray-200 rounded p-2 bg-gray-50">
                          <img
                            src={data.packaging_dislike_heatmaps[idx]}
                            alt={`Packaging ${idx + 1} - Dislike Heatmap`}
                            className="w-full h-auto rounded"
                          />
                        </div>
                      </div>
                    )} */}
                  {/* </div>
                </div>
              ))} */}

              {/* Shared Legend - shown once below all heatmaps */}
              {/* {data.heatmap_legend && (
                <div className="mt-6 flex justify-center">
                  <div className="inline-block bg-white rounded border border-gray-200 p-3">
                    <img
                      src={data.heatmap_legend}
                      alt="Heatmap Legend - Color Intensity Scale"
                      className="h-auto"
                      style={{ maxWidth: '600px', width: '100%' }}
                    />
                  </div>
                </div>
              )} */}
            {/* </div>
          ) : null} */}

          {/* Representative Quotes */}
          {data.persona_reaction_insights.quotes && (
            <div className={spacing.subsectionGap}>
              <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                What Personas Said
              </h3>
              <p className={`${typography.smallDescription} mb-4`}>
                Direct quotes expressing what they loved and their concerns
              </p>

              <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap}`}>
                {/* Positive Quotes */}
                {data.persona_reaction_insights.quotes.representative_positive && Array.isArray(data.persona_reaction_insights.quotes.representative_positive) && data.persona_reaction_insights.quotes.representative_positive.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                      They Loved
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.persona_reaction_insights.quotes.representative_positive.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-green-50 rounded p-3 border border-green-200">
                          <p className={`${typography.body} italic text-green-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Negative Quotes */}
                {data.persona_reaction_insights.quotes.representative_negative && Array.isArray(data.persona_reaction_insights.quotes.representative_negative) && data.persona_reaction_insights.quotes.representative_negative.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsDown className="w-4 h-4 mr-2 text-red-600" />
                      Concerns Raised
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.persona_reaction_insights.quotes.representative_negative.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-red-50 rounded p-3 border border-red-200">
                          <p className={`${typography.body} italic text-red-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Persona Reactions Table - Dropdown */}
          {/* {data.persona_reaction_responses?.persona_responses && 
           data.persona_reaction_responses.persona_responses.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowReactionTable(!showReactionTable)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">View Detailed Persona Reactions</span>
                <svg 
                  className={`w-4 h-4 text-gray-600 transform transition-transform ${showReactionTable ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showReactionTable && (
                <div className="mt-3">
                  {(() => {
                    // Group responses by image_index
                    const responsesByImage: Record<string, any[]> = {};
                    data.persona_reaction_responses.persona_responses.forEach(response => {
                      const imageIndex = response.image_index !== undefined ? String(response.image_index) : '0';
                      if (!responsesByImage[imageIndex]) {
                        responsesByImage[imageIndex] = [];
                      }
                      responsesByImage[imageIndex].push(response);
                    });

                    // Sort image indices numerically
                    const sortedImageIndices = Object.keys(responsesByImage).sort((a, b) => 
                      parseInt(a) - parseInt(b)
                    );

                    return (
                      <div className="space-y-4">
                        {sortedImageIndices.map((imageIndex) => {
                          const responses = responsesByImage[imageIndex];
                          const tableData = convertToTableData(
                            responses,
                            `Image ${imageIndex} - Persona Reactions`
                          );
                          
                          // Helper function to get image label
                          const getImageLabel = (idx: number) => {
                            if (idx === 0) return "Front View";
                            return `Additional View ${idx}`;
                          };

                          return tableData ? (
                            <div key={imageIndex} className="border border-gray-200 rounded-lg p-4 bg-white">
                              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                                {getImageLabel(parseInt(imageIndex))} ({responses.length} responses)
                              </h3>
                              <CollapsibleTable
                                table={tableData}
                                isExporting={isPreparingExport}
                                showFullContent={true}
                                showExportButtons={true}
                                hideTitle={true}
                                images={data.packaging_images || []}
                              />
                            </div>
                          ) : null;
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )} */}
        </div>
      )}

      {/* 6. Side Panel Analysis */}
      {data.side_insights && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
            <Layout className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Side Analysis</h2>
              <p className="text-xs text-gray-500 mt-1">Consumer reactions to side views of the packaging</p>
            </div>
          </div>

          {/* Strategic Recommendation */}
          {data.side_insights.strategic_recommendation && (
            <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-blue-500 ${spacing.subsectionGap}`}>
              <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                <Lightbulb className="w-4 h-4 mr-2 text-blue-600" />
                Strategic Recommendation
              </h3>
              <p className={typography.body}>{data.side_insights.strategic_recommendation}</p>
            </div>
          )}

          {/* What Works & Opportunity Areas - Side by Side */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
            {/* What Works */}
            {data.side_insights.what_works && data.side_insights.what_works.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-green-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  What Works
                </h3>
                {renderList(data.side_insights.what_works)}
              </div>
            )}

            {/* Opportunity Areas */}
            {data.side_insights.opportunity_areas && data.side_insights.opportunity_areas.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-orange-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <Lightbulb className="w-4 h-4 mr-2 text-orange-600" />
                  Opportunity Areas
                </h3>
                {renderList(data.side_insights.opportunity_areas)}
              </div>
            )}
          </div>

          {/* Missing Information */}
          {data.side_insights.common_missing_info && data.side_insights.common_missing_info.length > 0 && (
            <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-yellow-500 ${spacing.subsectionGap}`}>
              <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                <Info className="w-4 h-4 mr-2 text-yellow-600" />
                Missing Information
              </h3>
              <p className={`${typography.smallDescription} mb-3`}>Information personas expected but couldn&apos;t find</p>
              {renderList(data.side_insights.common_missing_info)}
            </div>
          )}

          {/* Usage Confidence Themes */}
          {data.side_insights.usage_confidence_themes && data.side_insights.usage_confidence_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>How confident personas feel about using the product correctly</p>
              <BarChart
                data={data.side_insights.usage_confidence_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Usage Confidence Themes"
              />
            </div>
          )}

          {/* Trust Badge Themes */}
          {data.side_insights.trust_badge_themes && data.side_insights.trust_badge_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Trust badges, certifications, and endorsements personas noticed</p>
              <BarChart
                data={data.side_insights.trust_badge_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Trust Badge Themes"
              />
            </div>
          )}

          {/* What Personas Said */}
          {data.side_insights.quotes && (
            <div className={spacing.subsectionGap}>
              <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
                What Personas Said
              </h3>
              <p className={`${typography.smallDescription} mb-4`}>
                Direct quotes about side panel reactions
              </p>

              <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap}`}>
                {/* Positive Quotes */}
                {data.side_insights.quotes.representative_positive && Array.isArray(data.side_insights.quotes.representative_positive) && data.side_insights.quotes.representative_positive.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                      They Loved
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.side_insights.quotes.representative_positive.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-green-50 rounded p-3 border border-green-200">
                          <p className={`${typography.body} italic text-green-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Negative Quotes */}
                {data.side_insights.quotes.representative_negative && Array.isArray(data.side_insights.quotes.representative_negative) && data.side_insights.quotes.representative_negative.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsDown className="w-4 h-4 mr-2 text-red-600" />
                      Concerns Raised
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.side_insights.quotes.representative_negative.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-red-50 rounded p-3 border border-red-200">
                          <p className={`${typography.body} italic text-red-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 7. Back Panel Analysis */}
      {data.back_insights && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
            <Package className="w-5 h-5 text-indigo-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Back Panel Analysis</h2>
              <p className="text-xs text-gray-500 mt-1">Consumer reactions to back views of the packaging</p>
            </div>
          </div>

          {/* Core Barrier - Highlighted */}
          {data.back_insights.core_barrier && (
            <div className={`mb-6 p-4 bg-red-50 border border-red-200 rounded border-l-4 border-l-red-500`}>
              <h3 className="font-semibold text-red-900 mb-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                Core Purchase Barrier
              </h3>
              <p className="text-sm text-red-800">{data.back_insights.core_barrier}</p>
            </div>
          )}

          {/* Accelerators & Barriers Bar Charts */}
          {/* {data.back_insights.top_accelerators && data.back_insights.top_accelerators.length > 0 && (
            <div className={`${spacing.subsectionGap} ${spacing.cardPadding} ${styling.nestedCard}`}>
              <p className={`${typography.description} mb-4`}>Specific claims and ingredients that increase purchase likelihood</p>
              <BarChart
                data={data.back_insights.top_accelerators}
                xAxis="theme"
                yAxis="percentage"
                title="Accelerators (Sales Drivers)"
              />
            </div>
          )} */}

          {/* {data.back_insights.top_barriers && data.back_insights.top_barriers.length > 0 && (
            <div className={`${spacing.subsectionGap} ${spacing.cardPadding} ${styling.nestedCard}`}>
              <p className={`${typography.description} mb-4`}>Specific text or ingredients that decrease purchase likelihood</p>
              <BarChart
                data={data.back_insights.top_barriers}
                xAxis="theme"
                yAxis="percentage"
                title="Barriers (Sales Killers)"
              />
            </div>
          )} */}

          {/* Trust Builders & Trust Breakers - Side by Side */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap} ${spacing.subsectionGap}`}>
            {/* Trust Builders */}
            {data.back_insights.trust_builders && data.back_insights.trust_builders.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-green-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Trust Builders
                </h3>
                {renderList(data.back_insights.trust_builders)}
              </div>
            )}

            {/* Trust Breakers */}
            {data.back_insights.trust_breakers && data.back_insights.trust_breakers.length > 0 && (
              <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-red-500`}>
                <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                  Trust Breakers
                </h3>
                {renderList(data.back_insights.trust_breakers)}
              </div>
            )}
          </div>

          {/* Friction Issues - Readability & Findability */}
          {(data.back_insights.readability_issues?.length > 0 || data.back_insights.findability_issues?.length > 0) && (
            <div className={`${spacing.cardPadding} ${styling.nestedCard} border-l-4 border-l-orange-500 ${spacing.subsectionGap}`}>
              <h3 className={`${typography.cardTitle} mb-3 flex items-center`}>
                <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                Friction Points
              </h3>
              <p className={`${typography.smallDescription} mb-3`}>Issues affecting readability and information discovery</p>
              {data.back_insights.readability_issues && data.back_insights.readability_issues.length > 0 && (
                <div className="mb-3">
                  <h4 className={`${typography.smallTitle} mb-2 text-orange-700`}>Readability Issues</h4>
                  {renderList(data.back_insights.readability_issues)}
                </div>
              )}
              {data.back_insights.findability_issues && data.back_insights.findability_issues.length > 0 && (
                <div>
                  <h4 className={`${typography.smallTitle} mb-2 text-orange-700`}>Findability Issues</h4>
                  {renderList(data.back_insights.findability_issues)}
                </div>
              )}
            </div>
          )}

          {/* Purchase Anxiety Themes */}
          {data.back_insights.purchase_anxiety_themes && data.back_insights.purchase_anxiety_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>How personas feel about potential risks of purchasing</p>
              <BarChart
                data={data.back_insights.purchase_anxiety_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Purchase Anxiety Themes"
              />
            </div>
          )}

          {/* Brand Values Themes */}
          {data.back_insights.brand_values_themes && data.back_insights.brand_values_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Values personas perceive from the brand based on back panel messaging</p>
              <BarChart
                data={data.back_insights.brand_values_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Perceived Brand Values"
              />
            </div>
          )}

          {/* Values Alignment Themes */}
          {data.back_insights.values_alignment_themes && data.back_insights.values_alignment_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>How well brand values align with persona values</p>
              <BarChart
                data={data.back_insights.values_alignment_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Values Alignment Themes"
              />
            </div>
          )}

          {/* Repeat Purchase Themes */}
          {data.back_insights.repeat_purchase_themes && data.back_insights.repeat_purchase_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Likelihood of repeat purchase based on back panel information</p>
              <BarChart
                data={data.back_insights.repeat_purchase_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Repeat Purchase Likelihood"
              />
            </div>
          )}

          {/* Routine Fit Themes */}
          {data.back_insights.routine_fit_themes && data.back_insights.routine_fit_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>How well the product fits into daily/weekly routines</p>
              <BarChart
                data={data.back_insights.routine_fit_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Routine Fit Themes"
              />
            </div>
          )}

          {/* Brand Story Resonance Themes */}
          {data.back_insights.brand_story_resonance_themes && data.back_insights.brand_story_resonance_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>How the brand story or mission resonates with personas</p>
              <BarChart
                data={data.back_insights.brand_story_resonance_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Brand Story Resonance"
              />
            </div>
          )}

          {/* Guarantee Presence Themes */}
          {data.back_insights.guarantee_presence_themes && data.back_insights.guarantee_presence_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Guarantees, money-back promises, or safety assurances noticed</p>
              <BarChart
                data={data.back_insights.guarantee_presence_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Guarantee Presence Themes"
              />
            </div>
          )}

          {/* Cross-Sell Presence Themes */}
          {data.back_insights.cross_sell_presence_themes && data.back_insights.cross_sell_presence_themes.length > 0 && (
            <div className={spacing.subsectionGap}>
              <p className={`${typography.description} mb-4`}>Messaging about other products in the brand&apos;s range</p>
              <BarChart
                data={data.back_insights.cross_sell_presence_themes}
                xAxis="theme"
                yAxis="percentage"
                title="Cross-Sell Messaging Themes"
              />
            </div>
          )}

          {/* What Personas Said */}
          {data.back_insights.quotes && (
            <div className={spacing.subsectionGap}>
              <h3 className={`${typography.cardTitle} mb-2 flex items-center`}>
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                What Personas Said
              </h3>
              <p className={`${typography.smallDescription} mb-4`}>
                Direct quotes about back panel reactions
              </p>

              <div className={`grid grid-cols-1 md:grid-cols-2 ${spacing.cardGap}`}>
                {/* Positive Quotes */}
                {data.back_insights.quotes.representative_positive && Array.isArray(data.back_insights.quotes.representative_positive) && data.back_insights.quotes.representative_positive.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-green-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                      They Loved
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.back_insights.quotes.representative_positive.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-green-50 rounded p-3 border border-green-200">
                          <p className={`${typography.body} italic text-green-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Negative Quotes */}
                {data.back_insights.quotes.representative_negative && Array.isArray(data.back_insights.quotes.representative_negative) && data.back_insights.quotes.representative_negative.length > 0 && (
                  <div className={`${styling.nestedCard} ${spacing.cardPadding} border-l-4 border-l-red-500`}>
                    <h4 className={`${typography.cardTitle} mb-3 flex items-center`}>
                      <ThumbsDown className="w-4 h-4 mr-2 text-red-600" />
                      Concerns Raised
                    </h4>
                    <div className={spacing.itemGap}>
                      {data.back_insights.quotes.representative_negative.slice(0, 3).map((quote: string, idx: number) => (
                        <div key={idx} className="bg-red-50 rounded p-3 border border-red-200">
                          <p className={`${typography.body} italic text-red-900`}>&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackagingReviewV2Results;

